import { useCallback, useEffect, useMemo } from 'react'
import {
  ReactFlow,
  Background,
  BackgroundVariant,
  useNodesState,
  useEdgesState,
  useReactFlow,
  ReactFlowProvider,
  Handle,
  Position,
  type Node,
  type Edge,
  type NodeTypes,
  type EdgeTypes,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { cn } from '@trafiflow/shared-ui'
import {
  Brain,
  Video,
  Sparkles,
  Database,
  Zap,
  FileText,
} from 'lucide-react'
import dagre from 'dagre'
import PipelineControls from './PipelineControls'

// Pipeline types
export type PipelineType = 'onboarding' | 'storyboard' | 'all'

// Agent system definitions
export interface AgentDefinition {
  id: string
  label: string
  description: string
  category: 'onboarding' | 'storyboard' | 'swiper' | 'knowledge' | 'orchestrator'
  prompts: string[]
  icon: typeof Brain
  pipelines: PipelineType[] // Which pipelines use this agent
}

const AGENT_DEFINITIONS: AgentDefinition[] = [
  // Orchestrators
  {
    id: 'ad_generator',
    label: 'Ad Generator',
    description: 'Orchestrates the full ad generation pipeline',
    category: 'orchestrator',
    prompts: [],
    icon: Zap,
    pipelines: ['onboarding'],
  },
  {
    id: 'storyboard_generator',
    label: 'Storyboard Generator',
    description: 'Orchestrates video storyboard creation',
    category: 'orchestrator',
    prompts: [],
    icon: Zap,
    pipelines: ['storyboard'],
  },

  // Onboarding Agents
  {
    id: 'brand_discovery',
    label: 'Brand Discovery',
    description: 'Analyzes brand identity and positioning',
    category: 'onboarding',
    prompts: ['ad_generator.brand_discovery'],
    icon: Brain,
    pipelines: ['onboarding'],
  },
  {
    id: 'ad_analysis',
    label: 'Ad Analysis',
    description: 'Analyzes individual ads for patterns',
    category: 'onboarding',
    prompts: ['ad_generator.individual_ad_analysis', 'ad_generator.pattern_analysis'],
    icon: Brain,
    pipelines: ['onboarding'],
  },
  {
    id: 'competitor_intel',
    label: 'Competitor Intelligence',
    description: 'Discovers and analyzes competitors',
    category: 'onboarding',
    prompts: ['ad_generator.competitor_intel'],
    icon: Brain,
    pipelines: ['onboarding'],
  },
  {
    id: 'creative_agent',
    label: 'Creative Blueprint',
    description: 'Generates ad copy and creative approach',
    category: 'onboarding',
    prompts: ['ad_generator.blueprint_copy', 'ad_generator.variant_copy'],
    icon: Sparkles,
    pipelines: ['onboarding'],
  },

  // Storyboard Agents
  {
    id: 'video_analysis',
    label: 'Video Analysis',
    description: 'Analyzes video content for scenes',
    category: 'storyboard',
    prompts: ['storyboard.video_analysis', 'storyboard.product_bible_context'],
    icon: Video,
    pipelines: ['storyboard'],
  },
  {
    id: 'visual_description',
    label: 'Visual Description',
    description: 'Generates image prompts for scenes',
    category: 'storyboard',
    prompts: ['storyboard.visual_description'],
    icon: Video,
    pipelines: ['storyboard'],
  },

  // Swiper Agents - Apply swipe methodology
  {
    id: 'script_swipe',
    label: 'Script Swiping',
    description: 'Swipes entire storyboard for new product',
    category: 'swiper',
    prompts: ['storyboard.script_swipe'],
    icon: FileText,
    pipelines: ['storyboard'],
  },
  {
    id: 'hook_swipe',
    label: 'Hook Swiping',
    description: 'Swipes hook scenes with variations',
    category: 'swiper',
    prompts: ['storyboard.hook_swipe'],
    icon: FileText,
    pipelines: ['storyboard'],
  },
  {
    id: 'scene_copy',
    label: 'Scene Copy Swiping',
    description: 'Swipes individual scene copy',
    category: 'swiper',
    prompts: ['storyboard.scene_copy_swipe'],
    icon: FileText,
    pipelines: ['storyboard'],
  },
  {
    id: 'angle_hooks',
    label: 'Angle Hooks',
    description: 'Generates hook angle variations',
    category: 'swiper',
    prompts: ['storyboard.angle_hooks'],
    icon: FileText,
    pipelines: ['storyboard'],
  },

  // Knowledge Base - Used by both pipelines
  {
    id: 'product_bible',
    label: 'Product Bible',
    description: 'Central product knowledge store',
    category: 'knowledge',
    prompts: ['ad_generator.product_bible', 'storyboard.product_bible_context'],
    icon: Database,
    pipelines: ['onboarding', 'storyboard'],
  },
]

// Agent connections showing data/control flow
const AGENT_CONNECTIONS: Array<{ source: string; target: string; label?: string }> = [
  // Ad Generator Pipeline
  { source: 'ad_generator', target: 'brand_discovery', label: 'starts' },
  { source: 'brand_discovery', target: 'ad_analysis' },
  { source: 'ad_analysis', target: 'competitor_intel' },
  { source: 'competitor_intel', target: 'product_bible', label: 'feeds' },
  { source: 'product_bible', target: 'creative_agent', label: 'informs' },
  { source: 'creative_agent', target: 'storyboard_generator', label: 'triggers' },

  // Storyboard Pipeline
  { source: 'storyboard_generator', target: 'video_analysis', label: 'starts' },
  { source: 'video_analysis', target: 'script_swipe', label: 'benchmark' },
  { source: 'product_bible', target: 'video_analysis', label: 'context' },

  // Swipe Pipeline (post video analysis)
  { source: 'script_swipe', target: 'hook_swipe' },
  { source: 'script_swipe', target: 'scene_copy' },
  { source: 'hook_swipe', target: 'angle_hooks' },
  { source: 'script_swipe', target: 'visual_description' },

  // Knowledge feeds all swipe stages
  { source: 'product_bible', target: 'script_swipe', label: 'context' },
  { source: 'product_bible', target: 'hook_swipe', label: 'context' },
  { source: 'product_bible', target: 'scene_copy', label: 'context' },
  { source: 'product_bible', target: 'angle_hooks', label: 'context' },
]

const CATEGORY_STYLES = {
  orchestrator: {
    bg: 'bg-purple-500/20',
    border: 'border-purple-500',
    text: 'text-purple-600',
    headerBg: 'bg-purple-500',
  },
  onboarding: {
    bg: 'bg-blue-500/10',
    border: 'border-blue-500/50',
    text: 'text-blue-600',
    headerBg: 'bg-blue-500',
  },
  storyboard: {
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500/50',
    text: 'text-emerald-600',
    headerBg: 'bg-emerald-500',
  },
  swiper: {
    bg: 'bg-amber-500/10',
    border: 'border-amber-500/50',
    text: 'text-amber-600',
    headerBg: 'bg-amber-500',
  },
  knowledge: {
    bg: 'bg-rose-500/10',
    border: 'border-rose-500/50',
    text: 'text-rose-600',
    headerBg: 'bg-rose-500',
  },
}

interface AgentNodeData {
  agent: AgentDefinition
  isHighlighted: boolean
  onSelect?: () => void
}

function AgentNodeComponent({ data }: { data: AgentNodeData }) {
  const { agent, isHighlighted, onSelect } = data
  const styles = CATEGORY_STYLES[agent.category]
  const Icon = agent.icon

  return (
    <>
      <Handle type="target" position={Position.Left} className="!w-2 !h-2 !bg-gray-400" />

      <div
        onClick={onSelect}
        className={cn(
          'w-44 rounded-lg border-2 overflow-hidden transition-all duration-200',
          'cursor-pointer hover:scale-105 hover:shadow-lg',
          styles.bg,
          styles.border,
          isHighlighted && 'ring-2 ring-offset-2 ring-blue-500',
        )}
      >
        {/* Header */}
        <div className={cn('px-3 py-1.5 flex items-center gap-2', styles.headerBg)}>
          <Icon className="w-3.5 h-3.5 text-white" />
          <span className="text-xs font-semibold text-white truncate">{agent.label}</span>
        </div>

        {/* Body */}
        <div className="px-3 py-2">
          <p className="text-[10px] text-gray-600 line-clamp-2">{agent.description}</p>
          {agent.prompts.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {agent.prompts.slice(0, 2).map((p) => (
                <span
                  key={p}
                  className="px-1.5 py-0.5 text-[8px] bg-gray-100 text-gray-500 rounded"
                >
                  {p.split('.')[1] || p}
                </span>
              ))}
              {agent.prompts.length > 2 && (
                <span className="px-1.5 py-0.5 text-[8px] bg-gray-100 text-gray-500 rounded">
                  +{agent.prompts.length - 2}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      <Handle type="source" position={Position.Right} className="!w-2 !h-2 !bg-gray-400" />
    </>
  )
}

const nodeTypes: NodeTypes = {
  agentNode: AgentNodeComponent as unknown as NodeTypes[string],
}

const edgeTypes: EdgeTypes = {}

function applyDagreLayout(nodes: Node[], edges: Edge[]): Node[] {
  const dagreGraph = new dagre.graphlib.Graph()
  dagreGraph.setDefaultEdgeLabel(() => ({}))
  dagreGraph.setGraph({ rankdir: 'LR', nodesep: 60, ranksep: 80 })

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: 180, height: 90 })
  })

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target)
  })

  dagre.layout(dagreGraph)

  return nodes.map((node) => {
    const pos = dagreGraph.node(node.id)
    return {
      ...node,
      position: { x: pos.x - 90, y: pos.y - 45 },
    }
  })
}

interface AgentArchitectureGraphProps {
  highlightedPromptId?: string
  pipelineFilter?: PipelineType
  onAgentSelect?: (agent: AgentDefinition) => void
  className?: string
}

function AgentArchitectureGraphInner({
  highlightedPromptId,
  pipelineFilter = 'all',
  onAgentSelect,
  className,
}: AgentArchitectureGraphProps) {
  const { fitView } = useReactFlow()

  // Filter agents based on pipeline
  const filteredAgents = useMemo(() => {
    if (pipelineFilter === 'all') return AGENT_DEFINITIONS
    return AGENT_DEFINITIONS.filter((agent) => agent.pipelines.includes(pipelineFilter))
  }, [pipelineFilter])

  // Get filtered agent IDs for connection filtering
  const filteredAgentIds = useMemo(() => {
    return new Set(filteredAgents.map((a) => a.id))
  }, [filteredAgents])

  // Filter connections based on filtered agents
  const filteredConnections = useMemo(() => {
    return AGENT_CONNECTIONS.filter(
      (conn) => filteredAgentIds.has(conn.source) && filteredAgentIds.has(conn.target)
    )
  }, [filteredAgentIds])

  // Find which agent uses the highlighted prompt
  const highlightedAgentId = useMemo(() => {
    if (!highlightedPromptId) return null
    const agent = filteredAgents.find((a) => a.prompts.includes(highlightedPromptId))
    return agent?.id || null
  }, [highlightedPromptId, filteredAgents])

  // Create nodes from filtered agent definitions
  const initialNodes: Node[] = useMemo(() => {
    return filteredAgents.map((agent) => ({
      id: agent.id,
      type: 'agentNode',
      position: { x: 0, y: 0 },
      data: {
        agent,
        isHighlighted: agent.id === highlightedAgentId,
        onSelect: onAgentSelect ? () => onAgentSelect(agent) : undefined,
      },
    }))
  }, [highlightedAgentId, onAgentSelect, filteredAgents])

  // Create edges from filtered connections
  const initialEdges: Edge[] = useMemo(() => {
    return filteredConnections.map((conn, idx) => ({
      id: `${conn.source}-${conn.target}-${idx}`,
      source: conn.source,
      target: conn.target,
      type: 'smoothstep',
      animated: conn.source === highlightedAgentId || conn.target === highlightedAgentId,
      label: conn.label,
      labelStyle: { fontSize: 9, fill: '#666' },
      style: {
        stroke: conn.source === highlightedAgentId || conn.target === highlightedAgentId
          ? '#3b82f6'
          : '#9ca3af',
        strokeWidth: conn.source === highlightedAgentId || conn.target === highlightedAgentId
          ? 2
          : 1,
      },
    }))
  }, [highlightedAgentId, filteredConnections])

  // Apply layout
  const layoutedNodes = useMemo(() => {
    return applyDagreLayout(initialNodes, initialEdges)
  }, [initialNodes, initialEdges])

  const [nodes, setNodes, onNodesChange] = useNodesState(layoutedNodes as never[])
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges)

  useEffect(() => {
    setNodes(layoutedNodes as never[])
    setEdges(initialEdges)
  }, [layoutedNodes, initialEdges, setNodes, setEdges])

  useEffect(() => {
    const timer = setTimeout(() => {
      fitView({ padding: 0.15, duration: 200 })
    }, 100)
    return () => clearTimeout(timer)
  }, [fitView])

  const handleFitView = useCallback(() => {
    fitView({ padding: 0.15, duration: 300 })
  }, [fitView])

  return (
    <div className={cn('relative w-full h-full', className)}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitView
        fitViewOptions={{ padding: 0.15 }}
        minZoom={0.3}
        maxZoom={1.5}
        proOptions={{ hideAttribution: true }}
        className="bg-white"
      >
        <Background variant={BackgroundVariant.Dots} gap={20} size={1} color="#e5e7eb" />
        <PipelineControls onFitView={handleFitView} />
      </ReactFlow>

      {/* Legend */}
      <div className="absolute bottom-4 left-4 bg-white rounded-lg border border-gray-200 p-3 shadow-sm">
        <h4 className="text-xs font-semibold text-gray-700 mb-2">Agent Categories</h4>
        <div className="space-y-1.5">
          {Object.entries(CATEGORY_STYLES).map(([key, styles]) => (
            <div key={key} className="flex items-center gap-2">
              <div className={cn('w-3 h-3 rounded', styles.headerBg)} />
              <span className="text-[10px] text-gray-600 capitalize">{key}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default function AgentArchitectureGraph(props: AgentArchitectureGraphProps) {
  return (
    <ReactFlowProvider>
      <AgentArchitectureGraphInner {...props} />
    </ReactFlowProvider>
  )
}

export { AGENT_DEFINITIONS, AGENT_CONNECTIONS }
